<script setup lang="ts">
import GenericTagSelector from '@/components/charts/chart-creator/GenericTagSelector.vue'
import { TagSelector } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { ref, Ref, watch } from 'vue'
import { useMeasurementStore } from '@/store/measurements'
import _ from 'lodash'
import { useDisplay } from 'vuetify'

const props = defineProps<{ measurement: string }>()
const model = defineModel<TagSelector<string>[]>({ default: [] })

const { mobile } = useDisplay()

const tagSelectors: Ref<TagSelector<string>[]> = ref([])
const availableTags: Ref<string[]> = ref([])
const availableUnusedTags: Ref<string[]> = ref([])
const canAddMoreTagSelectors: Ref<boolean> = ref(true)

async function loadAvailableTags() {
  availableTags.value = await useMeasurementStore().loadAvailableTagKeyForMeasurement(undefined, props.measurement)
}

function addTagSelector() {
  tagSelectors.value.push({ tag: availableUnusedTags.value[0], useAll: true, groupByValues: true, allowEmptyValues: false })
}

function deleteTagSelector(index: number) {
  tagSelectors.value.splice(index, 1)
}

watch(
  () => props.measurement,
  async () => {
    tagSelectors.value = []
    await loadAvailableTags()
  },
  { immediate: true }
)

watch(
  tagSelectors,
  () => {
    if (_.isEqual(model.value, tagSelectors.value)) return
    model.value = tagSelectors.value
  },
  { deep: true, immediate: true }
)

watch(
  model,
  () => {
    if (_.isEqual(model.value, tagSelectors.value)) return
    tagSelectors.value = model.value
  },
  { deep: true, immediate: true }
)

watch(
  () => [tagSelectors, availableTags],
  () => {
    const usedTags = tagSelectors.value.filter(selector => !!selector).map(selector => selector.tag)
    availableUnusedTags.value = availableTags.value.filter(tag => !usedTags.includes(tag))
    canAddMoreTagSelectors.value = availableUnusedTags.value.length > 0
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-tag-multiple"
        start
      />
      {{ $t('chart_sharing.creator.customize.tag_selectors.multi.title') }}
    </v-card-title>

    <v-card-text
      class="py-0"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <div
        v-if="tagSelectors.length === 0"
        class="mt-2 mb-8 d-flex justify-center opacity-70"
      >
        <v-icon
          icon="mdi-tag-off-outline"
          start
        />
        <span class="font-italic">
          {{ $t('chart_sharing.creator.customize.tag_selectors.multi.nothing_added_yet') }}
        </span>
      </div>

      <div v-if="tagSelectors.length > 0">
        <div
          v-for="(selector, index) in tagSelectors"
          :key="selector?.tag"
        >
          <GenericTagSelector
            v-model="tagSelectors[index]"
            :measurement="measurement"
            :available-tag-keys="[...availableUnusedTags, tagSelectors[index]?.tag]"
            :tag-index="index"
            @delete-tag-selector="deleteTagSelector"
          />

          <v-divider
            v-if="index < tagSelectors.length - 1"
            class="my-4"
          />
        </div>
      </div>

      <div
        v-if="canAddMoreTagSelectors"
        class="d-flex justify-center"
        :class="tagSelectors.length === 0 ? '' : 'mt-4'"
      >
        <v-btn
          prepend-icon="mdi-plus"
          variant="tonal"
          color="primary"
          @click="addTagSelector"
        >
          {{ $t('chart_sharing.creator.customize.tag_selectors.multi.add') }}</v-btn
        >
      </div>
    </v-card-text>
  </v-layout-card>
</template>
